import React, { useEffect, useState } from 'react';
import Select from 'react-select';
import Swal from 'sweetalert2';
import axiosInstance from '../utils/axiosInstance';

function initialsFromName(name) {
  if (!name) return '?';
  return name.trim().split(/\s+/).filter(Boolean).map((w) => w[0]).slice(0, 2).join('').toUpperCase();
}

function Avatar({ name, size = 'h-8 w-8' }) {
  return (
    <div className={`flex ${size} shrink-0 items-center justify-center rounded-full bg-indigo-100 text-xs font-bold text-indigo-700`}>
      {initialsFromName(name)}
    </div>
  );
}

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: '38px',
    borderRadius: '0.5rem',
    borderColor: state.isFocused ? '#818cf8' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(99,102,241,0.15)' : 'none',
    fontSize: '0.8125rem',
    '&:hover': { borderColor: '#818cf8' },
  }),
};

// Focused, single-purpose modal for the two things that typically change
// after a meeting is first created: who's actually coming/came, and when the
// next one is. Kept separate from the meeting editor page (action items,
// notes, PDF) so updating attendance or the next meeting date is a one-click
// trip from anywhere a meeting is referenced, not a detour through the whole
// meeting record. Shared between the Meetings list cards and the Meeting
// Editor page.
export default function AttendeesAndNextMeetingModal({ meetingId, staffOptions, onClose, onChanged }) {
  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newAttendee, setNewAttendee] = useState(null);
  const [nextMeetingDate, setNextMeetingDate] = useState('');
  const [nextMeetingTime, setNextMeetingTime] = useState('');

  const load = () => {
    setLoading(true);
    axiosInstance.get(`/admin/meetings/${meetingId}`)
      .then((resp) => {
        const m = resp.data?.meeting || null;
        setMeeting(m);
        setNextMeetingDate(m?.nextMeetingDate || '');
        setNextMeetingTime(m?.nextMeetingTime || '');
      })
      .catch(() => setMeeting(null))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [meetingId]);

  const toggleAttendance = async (staffId, attended) => {
    setBusy(true);
    try {
      await axiosInstance.patch(`/admin/meetings/${meetingId}/attendees/${staffId}`, { attended });
      load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const removeAttendee = async (staffId) => {
    setBusy(true);
    try {
      await axiosInstance.delete(`/admin/meetings/${meetingId}/attendees/${staffId}`);
      load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const addAttendee = async () => {
    if (!newAttendee) return;
    setBusy(true);
    try {
      await axiosInstance.post(`/admin/meetings/${meetingId}/attendees`, { staffId: newAttendee.value });
      setNewAttendee(null);
      load();
      onChanged();
    } catch (err) {
      Swal.fire('Error', err?.response?.data?.message || 'Failed to add attendee.', 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveNextMeeting = async () => {
    setBusy(true);
    try {
      await axiosInstance.patch(`/admin/meetings/${meetingId}`, {
        nextMeetingDate: nextMeetingDate || null,
        nextMeetingTime: nextMeetingTime || null,
      });
      load();
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const availableStaffOptions = meeting
    ? staffOptions.filter((s) => !meeting.attendees.some((a) => a.staffId === s.value))
    : staffOptions;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" onClick={onClose}>
      <div className="w-full max-w-md max-h-[85vh] overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {loading || !meeting ? (
          <div className="flex items-center justify-center py-16 text-slate-400">
            <svg className="h-7 w-7 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
            </svg>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-base font-bold text-slate-800">Attendees &amp; Next Meeting</h3>
                <p className="mt-0.5 text-xs text-slate-400">{meeting.title}</p>
              </div>
              <button onClick={onClose} className="shrink-0 rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600" aria-label="Close">
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="mt-4">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">
                Attendees ({meeting.attendees.filter((a) => a.attended === 1).length}/{meeting.attendees.length} present)
              </p>
              <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
                {meeting.attendees.map((a) => (
                  <li key={a.staffId} className="flex items-center gap-2 px-3 py-2">
                    <Avatar name={a.name} size="h-7 w-7" />
                    <span className="flex-1 min-w-0 truncate text-sm text-slate-700">{a.name}</span>
                    {[
                      { val: 1, label: 'Present', activeClass: 'border-emerald-300 bg-emerald-100 text-emerald-700' },
                      { val: 0, label: 'Absent', activeClass: 'border-red-300 bg-red-100 text-red-700' },
                    ].map(({ val, label, activeClass }) => (
                      <button
                        key={val}
                        disabled={busy}
                        onClick={() => toggleAttendance(a.staffId, val === 1)}
                        className={`shrink-0 rounded-lg border px-2 py-1 text-[11px] font-semibold disabled:opacity-50 ${
                          a.attended === val ? activeClass : 'border-slate-200 text-slate-500 hover:bg-slate-50'
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                    <button
                      disabled={busy}
                      onClick={() => removeAttendee(a.staffId)}
                      className="shrink-0 rounded-lg p-1 text-slate-300 hover:text-red-500 disabled:opacity-50"
                      aria-label={`Remove ${a.name}`}
                    >
                      <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </li>
                ))}
                {meeting.attendees.length === 0 && <li className="px-3 py-4 text-sm text-slate-400">No attendees added yet.</li>}
              </ul>
              <div className="mt-2 flex items-center gap-2">
                <div className="flex-1">
                  <Select value={newAttendee} onChange={setNewAttendee} options={availableStaffOptions} placeholder="Add attendee…" styles={selectStyles} />
                </div>
                <button onClick={addAttendee} disabled={!newAttendee || busy} className="shrink-0 rounded-lg bg-indigo-100 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-200 disabled:opacity-50">
                  Add
                </button>
              </div>
            </div>

            <div className="mt-5">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Next Meeting</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Date</label>
                  <input type="date" value={nextMeetingDate} onChange={(e) => setNextMeetingDate(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
                </div>
                <div>
                  <label className="mb-1 block text-xs font-semibold text-slate-500">Time</label>
                  <input type="time" value={nextMeetingTime} onChange={(e) => setNextMeetingTime(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100" />
                </div>
              </div>
              <p className="mt-1.5 text-[11px] text-slate-400">Shows on the /admin/reminder calendar automatically once saved.</p>
            </div>

            <div className="mt-5 flex justify-end gap-2">
              <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50">Close</button>
              <button onClick={saveNextMeeting} disabled={busy} className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50">
                {busy ? 'Saving…' : 'Save Next Meeting'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
